if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends HTMLElement {
        constructor() {
            super();

            this.input = this.querySelector('input[type="search"]');
            this.predictiveSearchResults = this.querySelector(
                '#predictive-search-target',
            );

            // Determine search type: 'native' (default) or 'metaobject'
            this.searchType = this.dataset.searchType || 'native';

            const raw = this.dataset.localeRoot || '/';
            this.localeRoot = raw.endsWith('/') ? raw : `${raw}/`;

            // Parse translations from data attribute
            this.translations = {};
            try {
                this.translations = JSON.parse(
                    this.dataset.translations || '{}',
                );
            } catch (e) {
                console.warn('Failed to parse translations:', e);
            }

            // Cache for metaobject data (only used for metaobject search)
            this.metaobjectData = null;
            this.dataLoaded = false;
            this.dataLoading = false;

            this.input.addEventListener(
                'input',
                this.debounce(() => this.onChange(), 300).bind(this),
            );

            // Lazy load metaobject data on focus if needed
            if (this.searchType === 'metaobject') {
                this.input.addEventListener(
                    'focus',
                    () => this.loadMetaobjectData(),
                    { once: true },
                );
            }
        }

        parsePredictiveSearchSectionJson(html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const jsonText = doc
                .querySelector('#shopify-section-predictive-search-data')
                ?.textContent?.trim();
            if (!jsonText) return null;
            try {
                return JSON.parse(jsonText);
            } catch (e) {
                console.error('Failed to parse predictive search JSON:', e);
                return null;
            }
        }

        async fetchPredictiveSearchDataPage(page) {
            const url =
                page <= 1
                    ? `${this.localeRoot}?section_id=predictive-search-data`
                    : `${this.localeRoot}?section_id=predictive-search-data&page=${page}`;
            const response = await fetch(url);
            const text = await response.text();
            return this.parsePredictiveSearchSectionJson(text);
        }

        async loadMetaobjectData() {
            if (this.dataLoaded || this.dataLoading) return;

            this.dataLoading = true;

            try {
                const first = await this.fetchPredictiveSearchDataPage(1);
                if (!first) {
                    this.dataLoading = false;
                    return;
                }

                const totalFaqPages = Math.max(
                    1,
                    Number(first.faq_pagination?.pages) || 1,
                );
                let faqList = [...(first.faq || [])];

                if (totalFaqPages > 1) {
                    const pagePromises = [];
                    for (let p = 2; p <= totalFaqPages; p++) {
                        pagePromises.push(
                            this.fetchPredictiveSearchDataPage(p).catch(
                                (err) => {
                                    console.warn(
                                        `Predictive search FAQ page ${p} failed:`,
                                        err,
                                    );
                                    return null;
                                },
                            ),
                        );
                    }
                    const rest = await Promise.all(pagePromises);
                    for (const pageData of rest) {
                        if (pageData?.faq?.length) {
                            faqList = faqList.concat(pageData.faq);
                        }
                    }
                }

                this.metaobjectData = {
                    ...first,
                    faq: this.dedupeByUrl(faqList),
                    faq_collection: this.dedupeByUrl(first.faq_collection),
                    product_support: this.dedupeByUrl(first.product_support),
                };
                this.dataLoaded = true;
            } catch (error) {
                console.error('Failed to load predictive search data:', error);
            }

            this.dataLoading = false;
        }

        onChange() {
            const searchTerm = this.input.value.trim();

            if (!searchTerm.length || searchTerm.length < 2) {
                this.close();
                return;
            }

            if (this.searchType === 'metaobject') {
                this.getMetaobjectResults(searchTerm);
            } else {
                this.getNativeResults(searchTerm);
            }
        }

        // Native search: products, articles, pages via /search/suggest
        getNativeResults(searchTerm) {
            const params = new URLSearchParams({
                q: searchTerm,
                'resources[type]': 'product,article,page',
                // variants.sku (product number) + variants.barcode (EAN) aren't searched by default
                'resources[options][fields]':
                    'title,body,product_type,tag,variants.title,variants.sku,variants.barcode,vendor',
                section_id: 'predictive-search-results',
            });

            fetch(`${this.localeRoot}search/suggest?${params.toString()}`)
                .then((response) => {
                    if (!response.ok) {
                        this.close();
                        throw new Error(
                            `${response.status} ${response.statusText}`,
                        );
                    }
                    return response.text();
                })
                .then((text) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(text, 'text/html');

                    const section = doc.querySelector(
                        '#shopify-section-predictive-search-results',
                    );

                    if (section) {
                        this.predictiveSearchResults.innerHTML =
                            section.innerHTML;
                        this.open();
                    } else {
                        this.close();
                    }
                })
                .catch((error) => {
                    console.error('Native search error:', error);
                    this.close();
                });
        }

        // Metaobject search: FAQ, FAQ collections, product support via client-side filtering
        async getMetaobjectResults(searchTerm) {
            // Ensure data is loaded
            if (!this.dataLoaded) {
                await this.loadMetaobjectData();
            }

            if (!this.metaobjectData) {
                this.close();
                return;
            }

            const [faq, faq_collection, product_support] = await Promise.all([
                this.filterItems(this.metaobjectData.faq, searchTerm),
                this.filterItems(
                    this.metaobjectData.faq_collection,
                    searchTerm,
                ),
                this.filterItems(
                    this.metaobjectData.product_support,
                    searchTerm,
                ),
            ]);
            const results = { faq, faq_collection, product_support };

            this.renderMetaobjectResults(results, searchTerm);
        }

        dedupeByUrl(items) {
            if (!items || !Array.isArray(items)) return [];

            const seen = new Set();
            return items.filter((item, index) => {
                if (!item) return false;

                const url = item.url != null ? String(item.url).trim() : '';
                const key =
                    url !== ''
                        ? url
                        : item.id != null && item.id !== ''
                          ? String(item.id)
                          : `_i_${index}`;

                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        async filterItems(items, searchTerm) {
            if (!items || !Array.isArray(items)) return [];
            const matches = await window.slayed.fuzzySearch(items, searchTerm);
            return this.dedupeByUrl(matches);
        }

        renderCategorySection(items, titleKey, defaultTitle, translations) {
            if (!items || items.length === 0) return '';

            // CSS classes - centralized for easy modification
            const CATEGORY_CLASSES =
                'predictive-search-category mb-8 pb-5 border-b';
            const HEADING_CLASSES = 'text-xs mb-2 text-gray-500';
            const LIST_CLASSES = 'space-y-2';
            const ITEM_CLASSES = 'py-3 mb-0';
            const LINK_CLASSES = 'truncate block';

            const title = this.escapeHtml(
                translations[titleKey] || defaultTitle,
            );
            let html = `
                <div class="${CATEGORY_CLASSES}">
                    <h3 class="${HEADING_CLASSES}">${title}</h3>
                    <ul role="listbox" class="${LIST_CLASSES}">
            `;

            for (const item of items) {
                html += `
                    <li role="option" class="${ITEM_CLASSES}">
                        <a href="${this.escapeHtml(item.url)}" class="${LINK_CLASSES}">${this.escapeHtml(item.title)}</a>
                    </li>
                `;
            }

            html += '</ul></div>';
            return html;
        }

        renderMetaobjectResults(results, searchTerm) {
            const t = this.translations;
            const hasResults =
                results.faq.length > 0 ||
                results.faq_collection.length > 0 ||
                results.product_support.length > 0;

            if (!hasResults) {
                this.predictiveSearchResults.innerHTML = `
                    <div id="predictive-search-results" tabindex="-1">
                        <p class="text-gray-500">${this.escapeHtml(t.no_results || 'No results found for')} "${this.escapeHtml(searchTerm)}"</p>
                        <button type="submit">${this.escapeHtml(t.search_for || 'Search for')} "${this.escapeHtml(searchTerm)}"</button>
                    </div>
                `;
                this.open();
                return;
            }

            let html = '<div id="predictive-search-results" tabindex="-1">';

            // Render all categories using the helper function
            html += this.renderCategorySection(results.faq, 'faq', 'FAQ', t);
            html += this.renderCategorySection(
                results.faq_collection,
                'faq_categories',
                'FAQ Categories',
                t,
            );
            html += this.renderCategorySection(
                results.product_support,
                'product_support',
                'Product Support',
                t,
            );

            html += `<button type="submit">${this.escapeHtml(t.search_for || 'Search for')} "${this.escapeHtml(searchTerm)}"</button></div>`;

            this.predictiveSearchResults.innerHTML = html;
            this.open();
        }

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text || '';
            return div.innerHTML;
        }

        open() {
            this.predictiveSearchResults.style.display = 'block';
            this.input.setAttribute('aria-expanded', 'true');
        }

        close() {
            this.predictiveSearchResults.style.display = 'none';
            this.input.setAttribute('aria-expanded', 'false');
        }

        debounce(fn, wait) {
            let t;
            return (...args) => {
                clearTimeout(t);
                t = setTimeout(() => fn.apply(this, args), wait);
            };
        }
    }

    customElements.define('predictive-search', PredictiveSearch);
}
